import TextInput from "@/forms/textinput";
import { Description } from "@/globals";
import { Fragment, useState } from "react";

import { FindingCloseReason, FindingState } from "@oneleet/api_client";

import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { Button } from "../../../..";
import { CloseReason } from "../expandedfinding";
import Status from "../status";

type Action = {
  value: CloseReason;
  label: string;
};

const actions: Array<Action> = [
  {
    value: FindingCloseReason.TEST_FOR_REMEDIATION,
    label: "Ready for review",
  },
  {
    value: FindingCloseReason.REJECTED,
    label: "Reject finding",
  },
  {
    value: FindingCloseReason.ACCEPTED_RISK,
    label: "Accept risk",
  },
  {
    value: FindingCloseReason.CLOSED_OTHER,
    label: "Close for other reason",
  },
];

type Props = {
  overrideOptions?: Array<Action>;
  status: FindingState;
  markRemediated: (reason: CloseReason, text?: string) => void;
};

export default function CloseAction(props: Props) {
  const [reason, setReason] = useState<CloseReason | null>();

  const [text, setText] = useState<string>();
  let label = "Please provide a reason for closing this finding:";

  if (reason && reason === FindingCloseReason.REJECTED) {
    label = "Please provide a reason for rejecting this finding:";
  } else if (reason && reason === FindingCloseReason.TEST_FOR_REMEDIATION) {
    label = "Provide any additional details needed for retesting (optional):";
  }

  const renderInnerPanel = () => {
    if (reason) {
      return (
        <Popover.Panel className="absolute left-[-8rem] z-10 mr-10 mt-5 flex w-[400px] -translate-x-1/2">
          {({ close }) => (
            <div className="flex w-[400px] flex-col gap-2 rounded-xl bg-white p-6 text-sm leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
              <Description>{label}</Description>
              <TextInput
                value={text || ""}
                onChange={setText}
                placeholder="Reason..."
              />
              <div className="mt-3 flex h-fit flex-row justify-between">
                <div
                  className="h-fit w-fit cursor-pointer pt-2 text-[0.8rem] text-gray-500"
                  onClick={() => {
                    setReason(null);
                  }}
                >
                  Back
                </div>
                <Button
                  label="Submit"
                  variant="solid"
                  color="brand"
                  onClick={() => {
                    props.markRemediated(reason, text || undefined);
                    close();
                  }}
                />
              </div>
            </div>
          )}
        </Popover.Panel>
      );
    }

    const finalActions = props.overrideOptions || actions;

    return (
      <Popover.Panel className="absolute left-[-1.5rem] z-10 mr-10 mt-5 flex w-fit max-w-fit -translate-x-1/2">
        <div className="min-w-fit rounded-xl bg-white px-3 py-1 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
          {finalActions.map((action) => (
            <div
              key={action.value}
              className="hover:text-brand-500 block w-fit cursor-pointer whitespace-nowrap p-2"
              onClick={() => {
                setReason(action.value);
              }}
            >
              {action.label}
            </div>
          ))}
        </div>
      </Popover.Panel>
    );
  };

  return (
    <Popover className="relative">
      <Popover.Button className="inline-flex items-center gap-x-1 border-transparent text-sm font-semibold leading-6 text-gray-900 focus:border-transparent focus:outline-none focus:ring-0">
        {/* <span className="mr-1 text-gray-700">{props.status}</span> */}
        <Status state={props.status} />
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        {renderInnerPanel()}
      </Transition>
    </Popover>
  );
}
